// =============================================================
// Data hooks — TanStack Query over the typed ApiClient. Every hook reads the
// REAL /api/v1 (no localStorage). Mutations invalidate the affected queries so
// the UI reflects the server after a domain action (booking, completion).
// =============================================================

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ACTIONABLE_TASK_STATUSES } from "@perpetuum/core";
import type {
  AuditLogFilters,
  BcMappingView,
  ChecklistItem,
  CustomFieldOptionRecord,
  Device,
  DeviceFile,
  DeviceHistoryEvent,
  DeviceMeasurementPoint,
  DowntimeConvertResult,
  GroupPermissions,
  MeasurementPointRecord,
  LocationNode,
  ReportFilters,
  ReportId,
  ReviewItem,
  SparePreflightReport,
  Spare,
  Stock,
  Task,
  WorkTimeEntry,
} from "@perpetuum/core";
import { useAuth } from "../app/AuthContext.js";
import type { QueryClient } from "@tanstack/react-query";

/**
 * Invalidate EVERY task-list cache after a task mutation. React Query matches
 * `["tasks"]` by prefix, so it already covers the tactical window (`["tasks"]`),
 * the actionable set (`["tasks","actionable",…]`), the by-status set
 * (`["tasks","by-status",…]`) and the per-device set (`["tasks","by-device",…]`).
 * What it does NOT cover is the strategic register pager, which lives under a
 * SEPARATE top-level key (`["tasks-page",…]`) — so after completing /
 * rescheduling a task the register would otherwise show stale rows. This helper
 * invalidates both families in one place, so no task mutation can drift out of
 * sync with one of the task surfaces (Wave-2 audit fix).
 */
function invalidateAllTasks(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: ["tasks"] });
  void qc.invalidateQueries({ queryKey: ["tasks-page"] });
}

export function useLocations() {
  const { api } = useAuth();
  return useQuery({
    queryKey: ["locations"],
    queryFn: async () => (await api.locations()).data as LocationNode[],
  });
}

export function useDevices() {
  const { api } = useAuth();
  return useQuery({
    queryKey: ["devices"],
    queryFn: async () => (await api.devices()).data as Device[],
  });
}

export function useTasks() {
  const { api } = useAuth();
  return useQuery({
    queryKey: ["tasks"],
    queryFn: async () => (await api.tasks()).data as Task[],
  });
}

/**
 * The ACTIONABLE task set — non-completed work (open / in_progress / paused /
 * postponed) only, with a generous limit so all of it lands in one page. The
 * generic `useTasks()` loads a bounded window that, on live data, is dominated
 * by the completed historical backlog (thousands of rows), so the PM Scheduler
 * and the Work board's open tabs would otherwise see ZERO actionable tasks.
 * This hook asks the server (`?status=open,in_progress,paused,postponed`) for
 * exactly the set those surfaces need, ordered actionability-first server-side.
 *
 * `limit` defaults to 1000 (well above the ~hundreds of open tasks a plant
 * carries). The query key includes the status set + limit so it caches
 * distinctly from the tactical `["tasks"]` window.
 */
const ACTIONABLE_STATUS_PARAM = ACTIONABLE_TASK_STATUSES.join(",");
export function useActionableTasks(limit = 1000) {
  const { api } = useAuth();
  return useQuery({
    queryKey: ["tasks", "actionable", ACTIONABLE_STATUS_PARAM, limit],
    queryFn: async () =>
      (await api.tasks({ status: ACTIONABLE_STATUS_PARAM, limit })).data as Task[],
  });
}

/**
 * A single page of the tasks register (GET /tasks?limit&offset). The strategic
 * register pages server-side over the FULL task set, distinct from the tactical
 * board's useTasks() (which loads a working window). `total` is the server's
 * reported count when present; otherwise the caller falls back to the page len.
 */
export interface TasksPage {
  rows: Task[];
  total: number | null;
}
export function useTasksPage(limit: number, offset: number) {
  const { api } = useAuth();
  return useQuery({
    queryKey: ["tasks-page", limit, offset],
    queryFn: async (): Promise<TasksPage> => {
      const res = await api.list<Task>("tasks", { limit, offset });
      // The server now returns the real `total` (COUNT(*) with the same WHERE,
      // ignoring limit/offset) in the list envelope. Read it honestly when
      // present; never fabricate a count when it is absent.
      const total = typeof res.total === "number" ? res.total : null;
      return { rows: res.data as Task[], total };
    },
    placeholderData: (prev) => prev,
  });
}

/**
 * Tasks filtered by an explicit status set (server-side `?status=`), paged with
 * a generous limit. Used by the Work board's "Completed" tab to load the
 * historical set on demand (it isn't part of the actionable default load).
 * `enabled` lets the caller defer the fetch until the tab is actually viewed.
 */
export function useTasksByStatus(statuses: readonly string[], opts: { enabled?: boolean; limit?: number } = {}) {
  const { api } = useAuth();
  const status = statuses.join(",");
  const limit = opts.limit ?? 1000;
  return useQuery({
    queryKey: ["tasks", "by-status", status, limit],
    enabled: opts.enabled ?? true,
    queryFn: async () => (await api.tasks({ status, limit })).data as Task[],
  });
}

/**
 * The work orders for ONE device (server-side `?deviceId=` filter). The asset
 * tree's Device Card needs only the selected device's tasks; fetching them
 * server-side (parameterized + tenant-scoped) avoids pulling the whole tenant's
 * task table and filtering it client-side (which does not scale past thousands
 * of rows). `enabled` defers the fetch until a device is actually selected.
 */
export function useDeviceTasks(deviceId: string | null) {
  const { api } = useAuth();
  return useQuery({
    queryKey: ["tasks", "by-device", deviceId],
    enabled: !!deviceId,
    queryFn: async () => (await api.tasks({ deviceId: deviceId!, limit: 500 })).data as Task[],
  });
}

export function useSpares() {
  const { api } = useAuth();
  return useQuery({
    queryKey: ["spares"],
    queryFn: async () => (await api.spares()).data as Spare[],
  });
}

export function useStocks() {
  const { api } = useAuth();
  return useQuery({
    queryKey: ["stocks"],
    queryFn: async () => (await api.stocks()).data as Stock[],
  });
}

/** Per-warehouse balances (in_stock + booked) for a spare. */
export function useSpareLevels(spareId: string | null) {
  const { api } = useAuth();
  return useQuery({
    queryKey: ["spare-levels", spareId],
    enabled: !!spareId,
    queryFn: async () => (await api.spareLevels(spareId!)).data,
  });
}

/** Live Available for one spare at one stock (derived server-side). */
export function useSpareAvailable(spareId: string | null, stockId: string | null) {
  const { api } = useAuth();
  return useQuery({
    queryKey: ["spare-available", spareId, stockId],
    enabled: !!spareId && !!stockId,
    queryFn: async () => (await api.spareAvailable(spareId!, stockId!)).available,
  });
}

/** Book qty of a spare at a stock; invalidates the Available + spares list. */
export function useBookSpare() {
  const { api } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { spareId: string; stockId: string; quantity: number }) =>
      api.spareBook(v.spareId, { stockId: v.stockId, quantity: v.quantity }),
    onSuccess: (_data, v) => {
      void qc.invalidateQueries({ queryKey: ["spare-available", v.spareId, v.stockId] });
      void qc.invalidateQueries({ queryKey: ["spare-levels", v.spareId] });
      void qc.invalidateQueries({ queryKey: ["spares"] });
    },
  });
}

/** Compute a report (real aggregation from /api/v1/reports/:id) with filters. */
export function useReport(id: ReportId, filters: ReportFilters, enabled = true) {
  const { api } = useAuth();
  return useQuery({
    queryKey: ["report", id, filters],
    enabled,
    queryFn: () => api.report(id, filters),
  });
}

/** Complete a task (server enforces the LOTO gate); invalidates the task list. */
export function useCompleteTask() {
  const { api } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => api.completeTask(taskId),
    onSuccess: () => invalidateAllTasks(qc),
  });
}

// ---------------------------------------------------------------------------
// WAVE 3 — Work-order execution surface. The detail drawer reads the full task
// row (for lotoConfirmed/signed/description, which the list rows don't carry),
// its checklist + work-time intervals, and writes through the new endpoints.
// Every mutation invalidates the affected query so the UI reflects the server.
// ---------------------------------------------------------------------------

/**
 * The FULL task row (generic CRUD GET /tasks/:id) — carries server-only gate
 * fields (lotoConfirmed, signed) the list shape omits, so the drawer can mirror
 * the complete handler's preconditions honestly.
 */
export type TaskRow = Task & { lotoConfirmed?: boolean; signed?: boolean };
export function useTask(taskId: string | null) {
  const { api } = useAuth();
  return useQuery({
    queryKey: ["task", taskId],
    enabled: !!taskId,
    queryFn: () => api.get<TaskRow>("tasks", taskId!),
  });
}

/** Edit task notes (description) via PATCH /tasks/:id; invalidates task + list. */
export function useUpdateTaskNotes() {
  const { api } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { taskId: string; description: string }) =>
      api.update<TaskRow>("tasks", v.taskId, { description: v.description }),
    // The notes/description field is carried ONLY by the full task row (the
    // detail drawer, `["task", id]`); none of the list shapes render it. So we
    // invalidate just the detail — invalidating every task list here was a
    // redundant refetch that changed nothing visible (Wave-2 audit fix).
    onSuccess: (_d, v) => void qc.invalidateQueries({ queryKey: ["task", v.taskId] }),
  });
}

/** A task's checklist items (ordered by sortOrder). */
export function useTaskChecklist(taskId: string | null) {
  const { api } = useAuth();
  return useQuery({
    queryKey: ["task-checklist", taskId],
    enabled: !!taskId,
    queryFn: async () => (await api.taskChecklist(taskId!)).data as ChecklistItem[],
  });
}

/** Add a checklist item; invalidates the checklist. */
export function useAddChecklistItem() {
  const { api } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { taskId: string; label: string; sortOrder?: number }) =>
      api.addChecklistItem(v.taskId, { label: v.label, sortOrder: v.sortOrder }),
    onSuccess: (_d, v) => void qc.invalidateQueries({ queryKey: ["task-checklist", v.taskId] }),
  });
}

/** Update a checklist item (toggle done / rename / reorder); invalidates the checklist. */
export function useUpdateChecklistItem() {
  const { api } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { taskId: string; itemId: string; done?: boolean; label?: string; sortOrder?: number }) =>
      api.updateChecklistItem(v.taskId, v.itemId, { done: v.done, label: v.label, sortOrder: v.sortOrder }),
    onSuccess: (_d, v) => void qc.invalidateQueries({ queryKey: ["task-checklist", v.taskId] }),
  });
}

/** Delete a checklist item; invalidates the checklist. */
export function useDeleteChecklistItem() {
  const { api } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { taskId: string; itemId: string }) => api.deleteChecklistItem(v.taskId, v.itemId),
    onSuccess: (_d, v) => void qc.invalidateQueries({ queryKey: ["task-checklist", v.taskId] }),
  });
}

/** A task's work-time intervals (newest first; running ⇒ endedAt null). */
export function useTaskWorkTimes(taskId: string | null) {
  const { api } = useAuth();
  return useQuery({
    queryKey: ["task-worktimes", taskId],
    enabled: !!taskId,
    queryFn: async () => (await api.taskWorkTimes(taskId!)).data as WorkTimeEntry[],
  });
}

/** Start the current user's work timer; invalidates the work-time list. */
export function useStartWorkTime() {
  const { api } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => api.startWorkTime(taskId),
    onSuccess: (_d, taskId) => void qc.invalidateQueries({ queryKey: ["task-worktimes", taskId] }),
  });
}

/** Stop the current user's work timer; invalidates the work-time list. */
export function useStopWorkTime() {
  const { api } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => api.stopWorkTime(taskId),
    onSuccess: (_d, taskId) => void qc.invalidateQueries({ queryKey: ["task-worktimes", taskId] }),
  });
}

// ---------------------------------------------------------------------------
// Phase 9.1 — admin / config screens. Generic collection read + the new admin
// endpoints. Mutations invalidate the affected query so the UI re-reads the API.
// ---------------------------------------------------------------------------

/** A generic tenant-scoped row (the CRUD collections return loosely-typed rows). */
export type Row = Record<string, unknown> & { id: string; name?: string };

/** Read any CRUD collection (categories, stocks, users, …). */
export function useCollection<T extends Row = Row>(collection: string, enabled = true) {
  const { api } = useAuth();
  return useQuery({
    queryKey: ["collection", collection],
    enabled,
    queryFn: async () => (await api.list<T>(collection, { limit: 500 })).data,
  });
}

/** Create a row in a collection; invalidates the collection. */
export function useCreateRow(collection: string) {
  const { api } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api.create<Row>(collection, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["collection", collection] }),
  });
}

/** Patch a row in a collection; invalidates the collection. */
export function useUpdateRow(collection: string) {
  const { api } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; body: Record<string, unknown> }) => api.update<Row>(collection, v.id, v.body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["collection", collection] }),
  });
}

/** Delete a row in a collection; invalidates the collection. */
export function useDeleteRow(collection: string) {
  const { api } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.remove(collection, id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["collection", collection] }),
  });
}

/** Read the per-tenant feature flags. */
export function useFeatureFlags() {
  const { api } = useAuth();
  return useQuery({
    queryKey: ["feature-flags"],
    queryFn: async () => (await api.featureFlags()).data,
  });
}

/** Toggle a feature module; invalidates the flag set. */
export function useSetFeatureFlag() {
  const { api } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { moduleId: string; enabled: boolean }) => api.setFeatureFlag(v.moduleId, v.enabled),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["feature-flags"] }),
  });
}

/** Read CIDR allow/block rules. */
export function useIpRules() {
  const { api } = useAuth();
  return useQuery({
    queryKey: ["ip-rules"],
    queryFn: async () => (await api.ipRules()).data,
  });
}

/** Add an IP rule; invalidates the list. */
export function useAddIpRule() {
  const { api } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { kind: "allow" | "block"; cidr: string; reason?: string }) => api.addIpRule(v),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["ip-rules"] }),
  });
}

/** Remove an IP rule; invalidates the list. */
export function useRemoveIpRule() {
  const { api } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.removeIpRule(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["ip-rules"] }),
  });
}

/** Read the audit log (newest first), optionally filtered (date/actor/action). */
export function useAuditLog(filters: AuditLogFilters = {}) {
  const { api } = useAuth();
  return useQuery({
    queryKey: ["audit-log", filters],
    queryFn: async () => (await api.auditLog({ limit: 100, ...filters })).data,
  });
}

/** Mass-replace performer; invalidates tasks + audit. */
export function useMassReplace() {
  const { api } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { fromUserId: string; toUserId: string; locationId?: string; categoryId?: string; dryRun?: boolean }) =>
      api.massReplacePerformer(v),
    onSuccess: () => {
      invalidateAllTasks(qc);
      void qc.invalidateQueries({ queryKey: ["audit-log"] });
    },
  });
}

/** Bulk-reschedule tasks (by performer OR explicit ids); invalidates tasks + audit. */
export function useMassReschedule() {
  const { api } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: {
      dueDate: string;
      fromUserId?: string;
      taskIds?: string[];
      locationId?: string;
      categoryId?: string;
      avoidWeekend?: boolean;
      dryRun?: boolean;
    }) => api.massReschedule(v),
    onSuccess: () => {
      invalidateAllTasks(qc);
      void qc.invalidateQueries({ queryKey: ["audit-log"] });
    },
  });
}

/** Test an IP against the current rule set (admin IP screen). */
export function useTestIp() {
  const { api } = useAuth();
  return useMutation({
    mutationFn: (ip: string) => api.testIp(ip),
  });
}

/** Read the Dynamics 365 BC spare↔item mappings (live state). */
export function useBcMappings() {
  const { api } = useAuth();
  return useQuery({
    queryKey: ["bc-mappings"],
    queryFn: async () => (await api.bcMappings()).data as BcMappingView[],
  });
}

/** Update a user-group's permission matrix (GroupPermissions JSONB). */
export function useUpdateGroupPermissions() {
  const { api } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; permissions: GroupPermissions }) =>
      api.update<Row>("user-groups", v.id, { permissions: v.permissions }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["collection", "user-groups"] }),
  });
}

// ---------------------------------------------------------------------------
// Phase 9.2 — operational write. PM reschedule, downtime register, spares
// transfer + receipt + write-off + import preflight, device-card writes.
// ---------------------------------------------------------------------------

/** Reschedule a task (writes due_date); invalidates the tasks list. */
export function useRescheduleTask() {
  const { api } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { taskId: string; dueDate: string; avoidWeekend?: boolean }) =>
      api.rescheduleTask(v.taskId, { dueDate: v.dueDate, avoidWeekend: v.avoidWeekend }),
    onSuccess: () => invalidateAllTasks(qc),
  });
}

/** Goods receipt (real-time only); invalidates the spare's levels. */
export function useReceiveSpare() {
  const { api } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { spareId: string; stockId: string; quantity: number; supplier?: string; docNr?: string }) =>
      api.spareReceive(v.spareId, { stockId: v.stockId, quantity: v.quantity, supplier: v.supplier, docNr: v.docNr }),
    onSuccess: (_d, v) => void qc.invalidateQueries({ queryKey: ["spare-levels", v.spareId] }),
  });
}

/** Write-off with the non-negative cap; invalidates the spare's levels. */
export function useWriteOffSpare() {
  const { api } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { spareId: string; stockId: string; quantity: number }) =>
      api.spareWriteOff(v.spareId, { stockId: v.stockId, quantity: v.quantity }),
    onSuccess: (_d, v) => void qc.invalidateQueries({ queryKey: ["spare-levels", v.spareId] }),
  });
}

/** Stock-to-stock transfer (booking-aware); invalidates the spare's levels. */
export function useTransferSpare() {
  const { api } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { spareId: string; fromStockId: string; toStockId: string; quantity: number }) =>
      api.spareTransfer(v.spareId, { fromStockId: v.fromStockId, toStockId: v.toStockId, quantity: v.quantity }),
    onSuccess: (_d, v) => void qc.invalidateQueries({ queryKey: ["spare-levels", v.spareId] }),
  });
}

/** Run the spares Excel-import preflight (no writes). */
export function useSparesImportPreflight() {
  const { api } = useAuth();
  return useMutation<SparePreflightReport, unknown, { alldeviceId?: string; name?: string }[]>({
    mutationFn: (rows) => api.sparesImportPreflight(rows),
  });
}

/** Register (log) a downtime; invalidates the downtimes collection. */
export function useRegisterDowntime() {
  const { api } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { deviceId?: string; categoryId?: string; startedAt?: string; endedAt?: string; reason?: string }) =>
      api.registerDowntime(v),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["collection", "downtimes"] }),
  });
}

/** Read a device's attached file references. */
export function useDeviceFiles(deviceId: string | null) {
  const { api } = useAuth();
  return useQuery({
    queryKey: ["device-files", deviceId],
    enabled: !!deviceId,
    queryFn: async () => (await api.deviceFiles(deviceId!)).data as DeviceFile[],
  });
}

/** Attach a file reference to a device; invalidates the file list. */
export function useAddDeviceFile() {
  const { api } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { deviceId: string; url: string; filename: string; contentType?: string; sizeBytes?: number }) =>
      api.addDeviceFile(v.deviceId, { url: v.url, filename: v.filename, contentType: v.contentType, sizeBytes: v.sizeBytes }),
    onSuccess: (_d, v) => void qc.invalidateQueries({ queryKey: ["device-files", v.deviceId] }),
  });
}

/** Read a device's measurement points + latest reading & band. */
export function useDeviceMeasurementPoints(deviceId: string | null, enabled = true) {
  const { api } = useAuth();
  return useQuery({
    queryKey: ["device-mpoints", deviceId],
    enabled: enabled && !!deviceId,
    queryFn: async () => (await api.deviceMeasurementPoints(deviceId!)).data as DeviceMeasurementPoint[],
  });
}

/** Record a measurement reading; invalidates the points (band reacts). */
export function useAddMeasurementReading() {
  const { api } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { deviceId: string; pointId: string; value: number }) =>
      api.addMeasurementReading(v.deviceId, { pointId: v.pointId, value: v.value }),
    onSuccess: (_d, v) => void qc.invalidateQueries({ queryKey: ["device-mpoints", v.deviceId] }),
  });
}

// ---- measurement-template points (LL/L/H/HH schema editor, Wave 7) ---------

/** Read a template's measurement points (ordered). */
export function useMeasurementPoints(templateId: string | null, enabled = true) {
  const { api } = useAuth();
  return useQuery({
    queryKey: ["measurement-points", templateId],
    enabled: enabled && !!templateId,
    queryFn: async () => (await api.measurementPoints(templateId!)).data as MeasurementPointRecord[],
  });
}

/** Add a point to a template; invalidates the template's points. */
export function useAddMeasurementPoint() {
  const { api } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { templateId: string; name: string; unit?: string | null; ll: number; l: number; h: number; hh: number }) =>
      api.addMeasurementPoint(v.templateId, { name: v.name, unit: v.unit, ll: v.ll, l: v.l, h: v.h, hh: v.hh }),
    onSuccess: (_d, v) => void qc.invalidateQueries({ queryKey: ["measurement-points", v.templateId] }),
  });
}

/** Update a point (rename / unit / thresholds); invalidates the template's points. */
export function useUpdateMeasurementPoint() {
  const { api } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: {
      templateId: string;
      pointId: string;
      body: { name?: string; unit?: string | null; ll?: number; l?: number; h?: number; hh?: number };
    }) => api.updateMeasurementPoint(v.templateId, v.pointId, v.body),
    onSuccess: (_d, v) => void qc.invalidateQueries({ queryKey: ["measurement-points", v.templateId] }),
  });
}

/** Delete a point; invalidates the template's points. */
export function useDeleteMeasurementPoint() {
  const { api } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { templateId: string; pointId: string }) => api.deleteMeasurementPoint(v.templateId, v.pointId),
    onSuccess: (_d, v) => void qc.invalidateQueries({ queryKey: ["measurement-points", v.templateId] }),
  });
}

// ---- custom-field SELECT options (Settings custom-field editor, Wave 9) -----

/** Read a custom-field def's persisted SELECT options (ordered). */
export function useCustomFieldOptions(fieldId: string | null, enabled = true) {
  const { api } = useAuth();
  return useQuery({
    queryKey: ["custom-field-options", fieldId],
    enabled: enabled && !!fieldId,
    queryFn: async () => (await api.customFieldOptions(fieldId!)).data as CustomFieldOptionRecord[],
  });
}

/** Add a SELECT option to a def; invalidates the def's options. */
export function useAddCustomFieldOption() {
  const { api } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { fieldId: string; value: string }) => api.addCustomFieldOption(v.fieldId, v.value),
    onSuccess: (_d, v) => void qc.invalidateQueries({ queryKey: ["custom-field-options", v.fieldId] }),
  });
}

/** Remove a SELECT option; invalidates the def's options. */
export function useDeleteCustomFieldOption() {
  const { api } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { fieldId: string; optionId: string }) => api.deleteCustomFieldOption(v.fieldId, v.optionId),
    onSuccess: (_d, v) => void qc.invalidateQueries({ queryKey: ["custom-field-options", v.fieldId] }),
  });
}

// ---- downtime → work order convert (the Faults flow, Wave 9) ----------------

/** Convert a downtime into a work order; invalidates the tasks list + downtimes. */
export function useConvertDowntimeToTask() {
  const { api } = useAuth();
  const qc = useQueryClient();
  return useMutation<DowntimeConvertResult, unknown, string>({
    mutationFn: (downtimeId: string) => api.convertDowntimeToTask(downtimeId),
    onSuccess: () => {
      invalidateAllTasks(qc);
      void qc.invalidateQueries({ queryKey: ["collection", "downtimes"] });
    },
  });
}

/** Read a device's unified History timeline. */
export function useDeviceHistory(deviceId: string | null) {
  const { api } = useAuth();
  return useQuery({
    queryKey: ["device-history", deviceId],
    enabled: !!deviceId,
    queryFn: async () => (await api.deviceHistory(deviceId!)).data as DeviceHistoryEvent[],
  });
}

// ---------------------------------------------------------------------------
// Phase 9.3 — global chrome. Command-palette search, notification feed +
// mark-read, and the Review Center over the existing /sync/review-items.
// ---------------------------------------------------------------------------

/** Command-palette search (debounced by the caller); empty query → skip. */
export function useSearch(q: string, enabled = true) {
  const { api } = useAuth();
  const trimmed = q.trim();
  return useQuery({
    queryKey: ["search", trimmed],
    enabled: enabled && trimmed.length > 0,
    queryFn: async () => (await api.search(trimmed)).groups,
  });
}

/** The derived notification feed (overdue WOs / low stock / faults / sync). */
export function useNotifications() {
  const { api } = useAuth();
  return useQuery({
    queryKey: ["notifications"],
    queryFn: () => api.notifications(),
    // Light polling so the unread badge stays roughly live without PowerSync.
    refetchInterval: 30_000,
  });
}

/** Mark one notification (by key) or all read; invalidates the feed. */
export function useMarkNotificationsRead() {
  const { api } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (arg: { key: string } | { all: true }) => api.markNotificationsRead(arg),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
}

/** List parked Review-Center items (default: open). */
export function useReviewItems(status: "open" | "resolved" = "open") {
  const { api } = useAuth();
  return useQuery({
    queryKey: ["review-items", status],
    queryFn: async () => (await api.reviewItems(status)).data as ReviewItem[],
  });
}

/** Resolve a parked item (apply / discard); invalidates items + notifications. */
export function useResolveReviewItem() {
  const { api } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; resolution: "apply" | "discard" }) =>
      api.resolveReviewItem(v.id, v.resolution),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["review-items"] });
      void qc.invalidateQueries({ queryKey: ["notifications"] });
      invalidateAllTasks(qc);
    },
  });
}
